import { useCallback, useEffect, useMemo, useState } from 'react'
import { t } from '../i18n/dict'
import { describeError, problem, type Problem } from '../i18n/errors'
import { ApiClient, ApiError, type Me } from './client'

/**
 * Login is the consent (docs/10-plugin-plan.md). The plugin asks the API for a consent URL
 * with a `state`, opens it, and polls `claim` with that state until the daemon has exchanged
 * the code and the API has issued a session. The token lives in localStorage — the M0 spike
 * confirms it survives a reload in the iframe and the iOS webview; if it does not, the trader
 * consents again, which costs a tap and nothing else.
 */

const TOKEN_KEY = 'gatekeeper.session'
const STATE_KEY = 'gatekeeper.login-state'

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    // Private mode or a blocked store: the session simply does not persist.
  }
}

/**
 * What to put in front of the trader when the login could not even start.
 *
 * A login that fails silently is indistinguishable from a dead button: the trader taps Connect,
 * nothing moves, and they have nothing to tell the operator. So the headline says which of the
 * two it was, and the guardian's own words follow underneath for them to quote.
 */
function describeLoginFailure(error: unknown): Problem {
  return error instanceof ApiError
    ? problem(t('login.couldNotStart'), describeError(error).detail)
    : problem(t('login.unreachable'), error instanceof Error ? error.message : null)
}

export type SessionState =
  | { status: 'checking' }
  | { status: 'signed-out'; message?: Problem }
  | { status: 'signing-in'; message: string; url: string }
  | { status: 'signed-in'; me: Me }

export interface Session {
  state: SessionState
  api: ApiClient
  signIn(scope: 'accounts' | 'trading', openUrl: (url: string) => void): Promise<void>
  signOut(): Promise<void>
}

export function useSession(): Session {
  const [token, setToken] = useState<string | null>(() => readStorage(TOKEN_KEY))
  const [state, setState] = useState<SessionState>({ status: 'checking' })
  const api = useMemo(() => new ApiClient(token), [token])

  // A stored token is only a session once the API says so.
  useEffect(() => {
    let cancelled = false
    if (!token) {
      setState({ status: 'signed-out' })
      return undefined
    }

    let timer: ReturnType<typeof setTimeout> | undefined
    let attempt = 0

    const check = async () => {
      try {
        const me = await api.me()
        if (!cancelled) setState({ status: 'signed-in', me })
      } catch (error) {
        if (cancelled) return
        if (error instanceof ApiError && error.status === 401) {
          // The session really is gone: revoked by the operator, or past its sliding window.
          writeStorage(TOKEN_KEY, null)
          setToken(null)
          setState({ status: 'signed-out', message: problem(t('login.ended')) })
          return
        }
        // Anything else means the API could not be reached, which is not a reason to throw a
        // valid session away — that made an API restart log every trader out and demand a
        // fresh cTrader consent. Hold the token and keep asking.
        attempt += 1
        setState({ status: 'checking' })
        timer = setTimeout(() => void check(), Math.min(1_000 * 2 ** Math.min(attempt, 4), 15_000))
      }
    }

    void check()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [api, token])

  const signIn = useCallback(
    async (scope: 'accounts' | 'trading', openUrl: (url: string) => void) => {
      try {
        const started = await api.startLogin(scope)
        writeStorage(STATE_KEY, started.state)
        setState({ status: 'signing-in', message: t('login.consentHere'), url: started.url })
        openUrl(started.url)

        // Poll until claimed or gone. Ten minutes is the API's own limit.
        const deadline = Date.now() + 10 * 60_000
        while (Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, 1_500))
          const claim = await api.claimLogin(started.state)
          if (claim.status === 'done') {
            writeStorage(STATE_KEY, null)
            writeStorage(TOKEN_KEY, claim.token)
            setToken(claim.token)
            return
          }
          if (claim.status === 'gone') {
            writeStorage(STATE_KEY, null)
            // AuthFlow words each refusal differently — already used, expired, not invited — and
            // they all arrive as one status. The sentence is the server's; the headline is ours.
            setState({ status: 'signed-out', message: problem(t('login.refused'), claim.message) })
            return
          }
          setState({ status: 'signing-in', message: claim.message, url: started.url })
        }
        setState({ status: 'signed-out', message: problem(t('login.tookTooLong')) })
      } catch (error) {
        writeStorage(STATE_KEY, null)
        setState({ status: 'signed-out', message: describeLoginFailure(error) })
      }
    },
    [api],
  )

  const signOut = useCallback(async () => {
    await api.logout().catch(() => undefined)
    writeStorage(TOKEN_KEY, null)
    setToken(null)
  }, [api])

  return { state, api, signIn, signOut }
}
